import { RefObject, useCallback, useMemo, useState, useSyncExternalStore } from "react"
import type { GridColDef } from "@mui/x-data-grid-premium"
import {
  COMPACT_HEADER_MAX_LINES,
  DEFAULT_COLUMN_TYPE_WIDTH,
  GROUP_HEADER_BASE_HEIGHT,
  HEADER_MAX_LINES,
} from "../constants"
import { getFontLoadVersion, subscribeToFontLoading } from "../helpers/font-loading"
import { useIsCompactGrid } from "./use-is-compact-grid"
import { getColumnHeaderHeightProp, getHeaderLabelWidth, getHeaderLayout, getHeaderName, getLeafHeaderHeight, isGoToFinderColumn } from "../helpers/header-layout"

type ComputedWidths = Record<string, number>

interface ColumnWidthApi {
  getAllColumns?: () => { field: string, computedWidth: number }[]
  subscribeEvent?: (event: string, handler: () => void) => () => void
}

type HeaderColumn = GridColDef & { hasStackedHeaderIcons?: boolean }

interface UseHeaderLayoutOptions {
  apiRef: RefObject<ColumnWidthApi | null>
  columns: HeaderColumn[]
  dataGridRemountKey: string
}

const WIDTH_EVENTS = ["columnWidthChange", "columnsChange"]
const NO_WIDTHS: ComputedWidths = {}

interface WidthsSnapshot {
  gridKey: string | null
  fontLoadVersion: number
  widths: ComputedWidths
}

interface WidthsSource {
  api: ColumnWidthApi | null
  gridKey: string
}

function getDefinitionWidth(column: GridColDef): number {
  return Math.max(column.minWidth ?? 0, column.width ?? DEFAULT_COLUMN_TYPE_WIDTH)
}

function readWidths(api: ColumnWidthApi | null): ComputedWidths {
  if (!api?.getAllColumns) return NO_WIDTHS
  return Object.fromEntries(api.getAllColumns().map(column => [column.field, column.computedWidth]))
}

function isSameWidths(previous: ComputedWidths, next: ComputedWidths): boolean {
  const fields = Object.keys(next)
  return fields.length === Object.keys(previous).length && fields.every(field => previous[field] === next[field])
}

function createWidthsStore() {
  let snapshot: WidthsSnapshot = { gridKey: null, fontLoadVersion: getFontLoadVersion(), widths: NO_WIDTHS }

  const refresh = ({ api, gridKey }: WidthsSource) => {
    const fontLoadVersion = getFontLoadVersion()
    const widths = readWidths(api)
    if (snapshot.gridKey === gridKey && snapshot.fontLoadVersion === fontLoadVersion && isSameWidths(snapshot.widths, widths)) return false
    snapshot = { gridKey, fontLoadVersion, widths }
    return true
  }

  return {
    getSnapshot: () => snapshot,
    subscribe: (source: WidthsSource, onChange: () => void) => {
      const notifyIfChanged = () => {
        if (refresh(source)) onChange()
      }
      const unsubscribers = [
        ...WIDTH_EVENTS.map(event => source.api?.subscribeEvent?.(event, notifyIfChanged)),
        subscribeToFontLoading(notifyIfChanged),
      ]
      notifyIfChanged()
      return () => unsubscribers.forEach(unsubscribe => unsubscribe?.())
    },
  }
}

export function useHeaderLayout({ apiRef, columns, dataGridRemountKey }: UseHeaderLayoutOptions) {
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const maxLines = useIsCompactGrid(container) ? COMPACT_HEADER_MAX_LINES : HEADER_MAX_LINES

  const [widthsStore] = useState(createWidthsStore)
  const subscribeToWidths = useCallback(
    (onChange: () => void) => widthsStore.subscribe({ api: apiRef.current, gridKey: dataGridRemountKey }, onChange),
    [apiRef, widthsStore, dataGridRemountKey],
  )
  const widthsSnapshot = useSyncExternalStore(subscribeToWidths, widthsStore.getSnapshot)

  const leafHeaderHeight = useMemo(() => {
    const computedWidths = widthsSnapshot.gridKey === dataGridRemountKey ? widthsSnapshot.widths : NO_WIDTHS
    const tallestLineCount = Math.max(1, ...columns.map((column) => {
      const hasGoToFinder = isGoToFinderColumn(column)
      const labelWidth = getHeaderLabelWidth(computedWidths[column.field] ?? getDefinitionWidth(column), hasGoToFinder)
      return getHeaderLayout(getHeaderName(column), labelWidth, maxLines, !hasGoToFinder).lineCount
    }))
    return getLeafHeaderHeight(tallestLineCount, columns.some(column => column.hasStackedHeaderIcons))
  }, [columns, maxLines, widthsSnapshot, dataGridRemountKey])

  return {
    containerRef: setContainer,
    maxLines,
    columnHeaderHeight: getColumnHeaderHeightProp(leafHeaderHeight),
    columnGroupHeaderHeight: GROUP_HEADER_BASE_HEIGHT,
  }
}
